import { supabase } from "@/integrations/supabase/client";
import { deriveLegalName } from "@/lib/legalNameRule";

/**
 * Formation defaults, in one place.
 *
 * A formation row carries the EIN worksheet and the founders interview. The
 * starting values come from the settings the team keeps, so the coding basis
 * can change without a code change.
 */

export type SettingsMap = Record<string, string>;

export async function readAppSettings(): Promise<SettingsMap> {
  const { data } = await (supabase as any).from("app_settings").select("key, value");
  const out: SettingsMap = {};
  for (const row of data || []) out[String(row.key)] = String(row.value ?? "");
  return out;
}

export function addressOne(address: unknown): string {
  const a = (address ?? {}) as Record<string, string>;
  return [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(", "), a.zip]
    .filter(Boolean)
    .join(" ");
}

type MemberLike = {
  id: string;
  una_name?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  legal_full_name?: string | null;
  legal_address?: unknown;
  ca_address?: unknown;
  mea_signed_at?: string | null;
};

export function formationDefaults(member: MemberLike, settings: SettingsMap) {
  const orgAddress = addressOne(member.ca_address) || addressOne(member.legal_address);
  const careOf =
    member.legal_full_name ||
    [member.first_name, member.last_name].filter(Boolean).join(" ") ||
    null;

  return {
    member_record_id: member.id,
    legal_name: deriveLegalName(member.una_name || ""),
    org_type: settings.ein_default_org_type || "Other Non Profit/Tax Exempt Organizations",
    activity_category: settings.ein_default_activity_category || "Organization",
    activity_subtype: settings.ein_default_activity_subtype || "Religious",
    activity_text: settings.ein_default_activity_text || "religious association",
    state: "California",
    phone: member.phone ?? null,
    care_of_name: careOf,
    interview: {
      PresidentProper: settings.ein_default_president || "",
      SecretaryProper: settings.ein_default_secretary || "",
      OrgDate: member.mea_signed_at ? String(member.mea_signed_at).slice(0, 10) : "",
      AppointmentStart: "9:00 AM",
      MeetingLocation: orgAddress,
      OrgAddress: orgAddress,
    } as Record<string, string>,
  };
}

/** The plain labels for the founders interview. The keys never show. */
export const INTERVIEW_FIELDS: { key: string; label: string; type?: "date" | "text" }[] = [
  { key: "PresidentProper", label: "Founding President" },
  { key: "SecretaryProper", label: "Recording Secretary" },
  { key: "OrgDate", label: "Organization date", type: "date" },
  { key: "OrgAddress", label: "Organization address" },
  { key: "MeetingDate", label: "Meeting date", type: "date" },
  { key: "MeetingLocation", label: "Meeting place" },
  { key: "MeetingState", label: "Meeting state" },
  { key: "MeetingCounty", label: "Meeting county" },
  { key: "AppointmentStart", label: "Appointment time" },
  { key: "ResignationTime", label: "Resignation meeting time" },
  { key: "ResignationDate", label: "Resignation date", type: "date" },
];
